import { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { useDebounce } from '@/hooks/use-debounce';

interface SearchInputProps {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Debounce delay in ms (default 300) */
  delay?: number;
  /** Focus the input on mount — useful when the field is revealed
   *  by a click and the user expects the cursor to land there. */
  autoFocus?: boolean;
}

export function SearchInput({
  value,
  onValueChange,
  placeholder = 'Search...',
  className,
  delay = 300,
  autoFocus,
}: SearchInputProps): React.ReactElement {
  const [localValue, setLocalValue] = useState(value);
  const debouncedValue = useDebounce(localValue, delay);
  const lastEmitted = useRef(value);

  // Emit debounced value to parent
  useEffect(() => {
    if (debouncedValue !== lastEmitted.current) {
      lastEmitted.current = debouncedValue;
      onValueChange(debouncedValue);
    }
  }, [debouncedValue, onValueChange]);

  // Sync external value changes (e.g. URL param reset)
  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setLocalValue(value);
    }
  }, [value]);

  return (
    <div className={cn('relative', className)}>
      <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder={placeholder}
        className="h-8 pl-7 pr-7 text-sm"
        autoFocus={autoFocus}
      />
      {localValue && (
        <button
          type="button"
          onClick={() => {
            setLocalValue('');
            lastEmitted.current = '';
            onValueChange('');
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
          aria-label="Clear search"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
